import React, { useState, useEffect } from 'react';
import { Search, Save, CheckCircle, AlertCircle, Info, Database } from 'lucide-react';
import { adminAPI } from '../services/api';

const ReverseSkinMappingInterface = ({ databaseSkin, onMappingSaved, onSkip }) => {
  const [selectedDemoSkin, setSelectedDemoSkin] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [demoSkins, setDemoSkins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Auto-search when component mounts with database skin weapon
  useEffect(() => {
    if (databaseSkin?.weapon?.name) {
      loadDemoSkins('', databaseSkin.weapon.name);
    } else {
      loadDemoSkins();
    }
  }, [databaseSkin]);

  const loadDemoSkins = async (query = '', weapon = '') => {
    setSearchLoading(true);
    try {
      const response = await adminAPI.getAvailableDemoSkins(query, weapon);
      setDemoSkins(response.data.data || []);
    } catch (error) {
      console.error('Failed to load demo skins:', error);
      setDemoSkins([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      loadDemoSkins(value, databaseSkin?.weapon?.name || '');
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const handleSaveMapping = async () => {
    if (!selectedDemoSkin || !databaseSkin) return;

    setSaving(true);
    try {
      await adminAPI.saveSkinMapping(
        selectedDemoSkin.demo_skin_name,
        selectedDemoSkin.demo_weapon_name,
        databaseSkin.id,
        'admin'
      );
      
      onMappingSaved && onMappingSaved({
        databaseSkin,
        demoSkin: selectedDemoSkin
      });
    } catch (error) {
      console.error('Failed to save mapping:', error);
      alert('Failed to save mapping: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const getRarityBadgeClass = (rarity) => {
    const rarityClasses = {
      'Consumer Grade': 'bg-gray-400 text-white',
      'Industrial Grade': 'bg-blue-500 text-white',
      'Mil-Spec': 'bg-blue-600 text-white',
      'Restricted': 'bg-purple-500 text-white',
      'Classified': 'bg-pink-500 text-white',
      'Covert': 'bg-red-500 text-white',
      'Contraband': 'bg-yellow-500 text-black'
    };
    
    return rarityClasses[rarity] || 'bg-gray-400 text-white';
  };

  if (!databaseSkin) return null;

  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Database className="h-5 w-5 text-blue-400" />
          Reverse Map: Database Skin to Demo Skin
        </h3>
        
        {/* Database Skin Display */}
        <div className="bg-gray-700 rounded p-4 mb-4">
          <div className="flex items-center gap-4">
            {databaseSkin.image && (
              <img
                src={databaseSkin.image}
                alt={databaseSkin.name}
                className="w-16 h-16 object-contain bg-gray-600 rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div className="flex-1">
              <div className="text-white font-semibold text-lg">{databaseSkin.name}</div>
              <div className="text-gray-300">{databaseSkin.weapon?.name}</div>
              {databaseSkin.pattern?.name && (
                <div className="text-gray-400 text-sm">Pattern: {databaseSkin.pattern.name}</div>
              )}
              {(databaseSkin.pattern?.id || databaseSkin.paint_index) && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {databaseSkin.pattern?.id && (
                    <span className="bg-purple-600 px-2 py-1 rounded text-xs text-white">
                      ID: {databaseSkin.pattern.id}
                    </span>
                  )}
                  {databaseSkin.paint_index && (
                    <span className="bg-blue-600 px-2 py-1 rounded text-xs text-white">
                      Paint: {databaseSkin.paint_index}
                    </span>
                  )}
                </div>
              )}
              {databaseSkin.rarity?.name && (
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded mt-1 ${getRarityBadgeClass(databaseSkin.rarity.name)}`}>
                  {databaseSkin.rarity.name}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="text-sm text-gray-400 mb-2">
          💡 Select which demo skin name should map to this database skin:
        </div>
      </div>

      {/* Search Interface */}
      <div className="mb-6">
        <div className="text-sm text-gray-400 mb-2">Search Demo Skins:</div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search demo skin names..."
            className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {databaseSkin.weapon?.name && `Pre-filtered by weapon: ${databaseSkin.weapon.name}`}
        </div>
      </div>

      {/* Demo Skins List */}
      {searchLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto mb-6">
          {demoSkins.length > 0 ? (
            <div className="grid grid-cols-1 gap-2">
              {demoSkins.map((demoSkin, index) => (
                <button
                  key={`${demoSkin.demo_weapon_name}-${demoSkin.demo_skin_name}-${index}`}
                  onClick={() => setSelectedDemoSkin(demoSkin)}
                  className={`p-4 rounded border text-left transition-all ${
                    selectedDemoSkin?.demo_skin_name === demoSkin.demo_skin_name && 
                    selectedDemoSkin?.demo_weapon_name === demoSkin.demo_weapon_name
                      ? 'border-purple-500 bg-purple-900 bg-opacity-20'
                      : 'border-gray-600 bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="text-white font-medium">{demoSkin.demo_skin_name}</div>
                      <div className="text-gray-400 text-sm">{demoSkin.demo_weapon_name}</div>
                    </div>
                    {demoSkin.demo_weapon_name === databaseSkin.weapon?.name && (
                      <div className="text-green-400 text-xs font-semibold">
                        ✓ Weapon Match
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Info className="h-8 w-8 mx-auto mb-2" />
              <p>No demo skins found</p>
              {searchTerm && <p className="text-sm">Try a different search term</p>}
            </div>
          )}
        </div>
      )}

      {/* Selected Demo Skin Preview */}
      {selectedDemoSkin && (
        <div className="bg-gray-700 rounded p-4 mb-6">
          <div className="text-sm text-gray-400 mb-2">Selected Demo Skin:</div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="text-white font-semibold">{selectedDemoSkin.demo_skin_name}</div>
              <div className="text-gray-300">{selectedDemoSkin.demo_weapon_name}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-400">Will map to:</div>
              <div className="text-sm text-purple-400 font-medium">{databaseSkin.name}</div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleSaveMapping}
          disabled={!selectedDemoSkin || saving}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saving ? 'Saving...' : 'Save Reverse Mapping'}
        </button>
        
        <button
          onClick={onSkip}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
        >
          Skip
        </button>
      </div>
    </div>
  );
};

export default ReverseSkinMappingInterface;
